import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, SlideOut, SlideOutHeader, Skeleton } from '@aimsos/ui';

import { INSIGHT_STATUS_VARIANTS, INSIGHT_TREND_VARIANTS } from '../../constants/intelligence.js';
import { useInsight } from '../../hooks/useInsight.js';
import { useReportInsightIssue } from '../../hooks/useReportInsightIssue.js';
import ItemUnavailableState from '../item-slideout/ItemUnavailableState.jsx';
import { ErrorState } from '../states/index.js';
import InsightSources from './InsightSources.jsx';
import TraitDetailSection from './TraitDetailSection.jsx';
import TraitHistory from './TraitHistory.jsx';
import TraitUsedIn from './TraitUsedIn.jsx';
import { iconForInsightCategory } from './insightIcons.js';

/**
 * One insight in the profile slide-out (CT-5716): title, value, headline, the
 * status and trend chips, a sentence, Details (Window, Sample), Sources with
 * "Open in History", Recalculation (the payload's own text), Where it's used,
 * visibility, History and Report an issue.
 *
 * Registered for the `insight` kind of the item slide-out host. Shares the
 * section, history and used-in pieces with the trait slide-out (CT-5715).
 * "Open in History" leaves the profile slide-out for the History tab with
 * `?event=<id>`; CT-5724 reads that param.
 *
 * @param {object} props
 * @param {string} props.itemId — the insight id.
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {string|null} [props.entityTitle]
 * @param {() => void} props.onClose
 * @param {() => void} [props.onChanged] — after a report, so How to approach refreshes.
 * @param {(eventId: string) => void} [props.onOpenHistoryEvent]
 */
const InsightDetailSlideOut = memo(function InsightDetailSlideOut({
  itemId,
  entityType,
  entityId,
  entityTitle,
  onClose,
  onOpenHistoryEvent,
  onChanged,
}) {
  const { t } = useTranslation('unified-profiles');

  const { insight, loading, error, notFound, accessDenied, refetch, refresh } = useInsight({
    entityType,
    entityId,
    insightId: itemId,
  });
  const { report, reporting, reported } = useReportInsightIssue({
    entityType,
    entityId,
    insightId: itemId,
    onReported: refresh,
    onChanged,
  });

  const title = insight?.category
    ? t('intelligence.insights.detail.title', {
        category: t(`intelligence.insights.quickFilters.${insight.category}`),
      })
    : t('intelligence.insights.detail.fallbackTitle');

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={title}
        icon={iconForInsightCategory(insight?.category)}
        size="M"
        showClose
        onClose={onClose}
      />
    ),
    [title, insight?.category, onClose],
  );

  const body = () => {
    if (loading) {
      return (
        <div data-testid="insight-detail-loading" className="flex flex-col gap-3" aria-busy>
          <Skeleton width="40%" height="20px" borderRadius="4px" />
          <Skeleton width="70%" height="14px" borderRadius="4px" />
          <Skeleton width="90%" height="14px" borderRadius="4px" />
        </div>
      );
    }
    if (notFound || accessDenied) return <ItemUnavailableState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (!insight) return null;

    const confidence = insight.confidence != null ? Math.round(insight.confidence * 100) : null;
    const statusKey = insight.trend === 'emerging' || insight.trend === 'paused' ? insight.trend : 'active';

    return (
      <div data-testid="insight-detail" data-insight-id={insight.id} className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <span className="text-lg font-semibold text-content">{insight.title}</span>
          {insight.value ? <span className="text-sm font-medium text-content">{insight.value}</span> : null}
          {insight.headline ? <span className="text-sm text-content-secondary">{insight.headline}</span> : null}
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid="insight-status" data-variant={INSIGHT_STATUS_VARIANTS[statusKey]}>
              <Badge variant={INSIGHT_STATUS_VARIANTS[statusKey]} size="small">
                {confidence != null
                  ? t(`intelligence.insights.detail.status.${statusKey}WithConfidence`, { percent: confidence })
                  : t(`intelligence.insights.detail.status.${statusKey}`)}
              </Badge>
            </span>
            {insight.trend ? (
              <Badge variant={INSIGHT_TREND_VARIANTS[insight.trend] ?? 'neutral'} size="small">
                {t(`intelligence.insights.trend.${insight.trend}`)}
              </Badge>
            ) : null}
          </div>
          {insight.summary ? <p className="text-sm text-content-secondary">{insight.summary}</p> : null}
        </div>

        <TraitDetailSection title={t('intelligence.insights.detail.details')}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-1 text-sm">
            <dt className="text-content-muted">{t('intelligence.insights.detail.window')}</dt>
            <dd className="text-content">{insight.window ?? '—'}</dd>
            <dt className="text-content-muted">{t('intelligence.insights.detail.sample')}</dt>
            <dd className="text-content">{insight.sample ?? '—'}</dd>
          </dl>
        </TraitDetailSection>

        {insight.sources.length ? (
          <TraitDetailSection title={t('intelligence.insights.detail.sources')}>
            <InsightSources sources={insight.sources} onOpenInHistory={onOpenHistoryEvent} />
          </TraitDetailSection>
        ) : null}

        {insight.recalculationText ? (
          <TraitDetailSection title={t('intelligence.insights.detail.recalculation')}>
            <p className="text-sm text-content">{insight.recalculationText}</p>
          </TraitDetailSection>
        ) : null}

        <TraitDetailSection title={t('intelligence.traits.detail.usedIn')}>
          <TraitUsedIn usedIn={insight.usedIn} />
          {insight.visibility ? (
            <p className="text-sm text-content-secondary">
              {t(`intelligence.traits.detail.visibility.${insight.visibility}`, {
                name: entityTitle ?? t('profile.untitled'),
              })}
            </p>
          ) : null}
        </TraitDetailSection>

        {insight.history.length ? (
          <TraitDetailSection title={t('intelligence.traits.detail.history')}>
            <TraitHistory history={insight.history} />
          </TraitDetailSection>
        ) : null}
      </div>
    );
  };

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={title}>
      <div data-testid="insight-slideout" data-item-id={itemId} className="flex h-full min-h-0 flex-col gap-4">
        <div className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-y-auto">{body()}</div>

        {insight ? (
          <div className="flex shrink-0 items-center justify-between gap-3 border-t border-edge pt-4">
            <span className="text-xs text-content-muted">{t('intelligence.traits.detail.reportHint')}</span>
            <Button variant="secondary" size="sm" onClick={report} disabled={reporting || reported || insight.reportOpen}>
              {reported || insight.reportOpen
                ? t('intelligence.traits.detail.reported')
                : t('intelligence.traits.detail.report')}
            </Button>
          </div>
        ) : null}
      </div>
    </SlideOut>
  );
});

export default InsightDetailSlideOut;
